import { useCallback } from 'react'
import { Avo } from '@thriveglobal/thrive-web-tracking'
import getTrackingProps from './getTrackingProps'

type ContentSharedTrackingProps = Parameters<typeof Avo.contentShared>[0]
type ContentSharedTrackingFields =
    | 'contentId'
    | 'contentTitle'
    | 'contentUrl'
    | 'contentType'
    | 'pageSection'
    | 'activityType'

type UseContentSharedTrackingProps = Partial<
    Pick<ContentSharedTrackingProps, ContentSharedTrackingFields>
>

const defaultContentSharedProps: Partial<
    Omit<ContentSharedTrackingProps, ContentSharedTrackingFields>
> = {
    contentFormatType: null,
    contentSubtype: null,
    resetType: null,
    contentSource: null,
    featureType: 'library'
}

/**
 * Custom hook to track content shared events using Avo.
 *
 * This hook provides a function, `trackContentShared`, that triggers the `Avo.contentShared` event
 * with merged properties including both the default event props and the user-provided props.
 *
 * @returns {(props: UseContentSharedTrackingProps) => void} - A callback function that can be called
 * to track the content shared event. It accepts `UseContentSharedTrackingProps` which include
 * required event properties like `contentId`, `contentTitle`, `contentUrl`, etc.
 *
 * @example
 * const trackContentShared = useContentSharedTracking();
 * trackContentShared({
 *    contentId: '123',
 *    contentTitle: 'Article Title',
 *    contentUrl: 'https://app.thriveglobal.com/article',
 *    contentType: 'article',
 *    pageSection: 'bookmarks',
 * });
 */
const useContentSharedTracking = (): ((
    props: UseContentSharedTrackingProps
) => void) => {
    /**
     * Tracks a content shared event using the `Avo.contentShared` method.
     *
     * @param {UseContentSharedTrackingProps} props - The properties specific to the content shared event,
     * including `contentId`, `contentTitle`, `contentUrl`, `contentType`, and `pageSection`.
     */
    return useCallback((props: UseContentSharedTrackingProps) => {
        Avo.contentShared(
            getTrackingProps<ContentSharedTrackingProps>({
                ...defaultContentSharedProps,
                ...props
            })
        )
    }, [])
}

export default useContentSharedTracking
